let productMountObserver: MutationObserver | null = null;
let productMountTimer: number | undefined;
let accountObserver: MutationObserver | null = null;

const getProductContent = (): HTMLElement | null =>
  document.querySelector<HTMLElement>('#lootly-product-content');

const getProductHost = (): HTMLElement | null =>
  document.querySelector<HTMLElement>('#lootly-product-host');

const getProductMount = (): HTMLElement | null =>
  document.querySelector<HTMLElement>('[data-lootly-product-mount]');

const updateProductData = (mount: HTMLElement, content: HTMLElement): void => {
  const points = content.querySelector<HTMLElement>('#lootly-inline-points');

  if (!points) return;

  const price = mount.dataset.lootlyPrice;
  const productId = mount.dataset.lootlyProductId;

  if (price) {
    points.dataset.productPrice = price;
  }

  if (productId) {
    points.dataset.productId = productId;
  }

  /*
   * Never hide or recreate the Lootly target.
   * Lootly must retain the same initialized element.
   */
  points.hidden = false;
};

const mountProductLootly = (): boolean => {
  const content = getProductContent();
  const mount = getProductMount();

  if (!content || !mount) return false;

  updateProductData(mount, content);

  if (content.parentElement !== mount) {
    mount.append(content);
  }

  return true;
};

const parkProductLootly = (): void => {
  const content = getProductContent();
  const host = getProductHost();

  if (!content || !host) return;

  if (content.parentElement !== host) {
    host.append(content);
  }
};

const waitForProductMount = (previousMount: HTMLElement | null): void => {
  productMountObserver?.disconnect();
  window.clearTimeout(productMountTimer);

  if (!document.body) return;

  productMountObserver = new MutationObserver(() => {
    const nextMount = getProductMount();

    if (nextMount && nextMount !== previousMount) {
      mountProductLootly();

      productMountObserver?.disconnect();
      productMountObserver = null;

      window.clearTimeout(productMountTimer);
    }
  });

  productMountObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  /*
   * Fallback in case Neto updates the existing mount
   * instead of replacing it.
   */
  productMountTimer = window.setTimeout(() => {
    mountProductLootly();

    productMountObserver?.disconnect();
    productMountObserver = null;
  }, 1000);
};

const setupProductLootly = (): void => {
  if (!mountProductLootly()) {
    waitForProductMount(null);
  }
};

const getBalanceValue = (element: HTMLElement): number => {
  const match = element.textContent?.match(/\d[\d,]*/);

  return match ? Number.parseInt(match[0].replace(/,/g, ''), 10) : 0;
};

const setupAccountLootly = (): void => {
  accountObserver?.disconnect();

  const balance = document.querySelector<HTMLElement>(
    '#lootly-customer-point-balance',
  );

  const zeroBalance = document.querySelector<HTMLElement>(
    '.lootly-customer-point-balance-zero',
  );

  if (!balance || !zeroBalance) return;

  const update = (): void => {
    const balanceText = balance.textContent?.trim() ?? '';
    const hasPoints = getBalanceValue(balance) > 0;

    /*
     * Keep an empty Lootly target available while it initializes.
     * Hide it only when Lootly explicitly outputs zero.
     */
    balance.hidden = balanceText !== '' && !hasPoints;
    zeroBalance.hidden = hasPoints;
  };

  update();

  accountObserver = new MutationObserver(update);

  accountObserver.observe(balance, {
    childList: true,
    subtree: true,
    characterData: true,
  });
};

const checkLootly = {
  init(): void {
    setupProductLootly();
    setupAccountLootly();
  },
};

/*
 * Reset fires before Neto replaces the variation header.
 * Park the initialized Lootly element first, then mount it
 * inside the new header.
 */
window.addEventListener('reset', () => {
  if (!getProductContent()) return;

  const previousMount = getProductMount();

  parkProductLootly();
  waitForProductMount(previousMount);
});

export default checkLootly;
